import { useEffect, useMemo, useState } from "react";
import { hasRole, isFinanceLead } from "@/lib/roles";
import api, { formatApiError } from "@/lib/api";
import { employeeMatches } from "@/lib/employeeSearch";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Check, Ban, Trash2, FileDown, Send, Search, Receipt, FileSpreadsheet } from "lucide-react";

function fmt(n) {
    if (n == null) return "₦0";
    return "₦" + Number(n).toLocaleString("en-NG", { maximumFractionDigits: 0 });
}

const STATUS_PILLS = {
    pending_dept_approval: { label: "Dept review", cls: "bg-yellow-50 text-yellow-700 border-yellow-200" },
    pending_finance_approval: { label: "Finance review", cls: "bg-purple-50 text-purple-700 border-purple-200" },
    pending_disbursement: { label: "Awaiting payout", cls: "bg-blue-50 text-blue-700 border-blue-200" },
    disbursed: { label: "Disbursed", cls: "bg-green-50 text-green-700 border-green-200" },
    rejected: { label: "Rejected", cls: "bg-red-50 text-red-700 border-red-200" },
};

function StatusPill({ status }) {
    const s = STATUS_PILLS[status] || { label: status, cls: "bg-ink/5 text-ink/70 border-ink/10" };
    return (
        <span className={`inline-flex items-center px-2 py-1 border text-[11px] uppercase tracking-[0.15em] ${s.cls}`}>
            {s.label}
        </span>
    );
}

export default function Settlements() {
    const { user } = useAuth();
    const role = user?.role || "";
    const isFinLead = isFinanceLead(user);
    const canCreate = hasRole(user, "admin", "hr");
    const canDeptApprove = hasRole(user, "admin", "department_lead");
    const canFinanceApprove = hasRole(user, "admin") || isFinLead;
    const canDisburse = hasRole(user, "admin", "accountant");
    const canDelete = hasRole(user, "admin");

    const [items, setItems] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [statusFilter, setStatusFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ employee_id: "", kind: "resignation", end_date: "", note: "" });
    const [empSearch, setEmpSearch] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: list }, { data: emps }] = await Promise.all([
                api.get("/settlements", {
                    params: { status: statusFilter !== "all" ? statusFilter : undefined },
                }),
                api.get("/employees"),
            ]);
            setItems(list);
            setEmployees(emps);
        } catch (err) {
            toast.error("Failed to load settlements");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

    const filteredEmployees = useMemo(() => {
        const q = empSearch.trim().toLowerCase();
        if (!q) return employees.slice(0, 30);
        return employees.filter((e) => employeeMatches(e, q)).slice(0, 30);
    }, [empSearch, employees]);

    const selectedEmp = useMemo(
        () => employees.find((e) => e.id === form.employee_id),
        [form.employee_id, employees],
    );

    const onCreate = async (e) => {
        e.preventDefault();
        if (!form.employee_id) { toast.error("Pick an employee"); return; }
        setCreating(true);
        try {
            await api.post("/settlements", form);
            toast.success("Settlement initiated. Department lead notified.");
            setOpen(false);
            setForm({ employee_id: "", kind: "resignation", end_date: "", note: "" });
            setEmpSearch("");
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not initiate");
        } finally {
            setCreating(false);
        }
    };

    const action = async (s, kind, prompt) => {
        let body = {};
        if (kind === "reject") {
            const reason = window.prompt(prompt || "Reason for rejection?");
            if (!reason) return;
            body = { reason };
        } else {
            const note = window.prompt(prompt || "Add a note (optional)") || "";
            body = { note };
        }
        try {
            await api.put(`/settlements/${s.id}/${kind === "reject" ? "reject" : kind}`, body);
            toast.success(`Settlement ${kind.replace("-", " ")}`);
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Action failed");
        }
    };

    const onDelete = async (s) => {
        if (!window.confirm(`Delete settlement for ${s.employee_name}? This cannot be undone.`)) return;
        try {
            await api.delete(`/settlements/${s.id}`);
            toast.success("Deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
        }
    };

    const downloadPdf = async (s) => {
        try {
            const res = await api.get(`/settlements/${s.id}/pdf`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `settlement_${s.kind}_${(s.employee_name || "employee").replace(/\s+/g, "_")}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toast.error("PDF download failed");
        }
    };

    const downloadAdvice = async (s) => {
        try {
            const res = await api.get(`/settlements/${s.id}/bank-advice`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `bank_advice_settlement_${(s.employee_name || "employee").replace(/\s+/g, "_")}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Bank advice unavailable");
        }
    };

    return (
        <div className="px-8 md:px-12 py-12 md:py-16" data-testid="settlements-page">
            <div className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <div className="overline">Exit benefits</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-4xl sm:text-5xl text-ink leading-[1.02]">
                        Full &amp; Final Settlements
                    </h1>
                    <p className="mt-3 text-sm text-ink/60 max-w-xl">
                        Resignation and termination settlements with three-stage approval — Department → Finance → Accountant.
                    </p>
                </div>
                {canCreate && (
                    <button
                        onClick={() => setOpen(true)}
                        className="inline-flex items-center gap-2 bg-klein text-white px-5 py-3 text-sm hover:bg-klein/90"
                        data-testid="add-settlement-button"
                    >
                        <Plus className="w-4 h-4" /> Initiate F&F
                    </button>
                )}
            </div>

            <div className="mt-8 flex items-center gap-2 flex-wrap">
                {["all", "pending_dept_approval", "pending_finance_approval", "pending_disbursement", "disbursed", "rejected"].map((s) => (
                    <button
                        key={s}
                        onClick={() => setStatusFilter(s)}
                        className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] border ${
                            statusFilter === s ? "bg-ink text-white border-ink" : "border-ink/15 text-ink/70 hover:border-ink/40"
                        }`}
                        data-testid={`settlement-filter-${s}`}
                    >
                        {(STATUS_PILLS[s]?.label || "All").replace("Dept ", "Dept ")}
                    </button>
                ))}
            </div>

            <div className="mt-6 bg-white border border-ink/10 overflow-auto max-h-[520px]">
                <table className="w-full border-collapse text-sm">
                    <thead>
                        <tr className="border-b-2 border-ink/20 bg-bone">
                            <th className="text-left overline px-4 py-3">Employee</th>
                            <th className="text-left overline px-4 py-3">Type</th>
                            <th className="text-left overline px-4 py-3 hidden md:table-cell">Effective</th>
                            <th className="text-right overline px-4 py-3">Net</th>
                            <th className="text-left overline px-4 py-3">Stage</th>
                            <th className="text-right overline px-4 py-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="6" className="px-6 py-16 text-center text-ink/40">Loading…</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan="6" className="px-6 py-16 text-center text-ink/40">No settlements yet.</td></tr>
                        ) : items.map((s) => (
                            <tr key={s.id} className="border-b border-ink/10 hover:bg-bone/40" data-testid={`settlement-row-${s.id}`}>
                                <td className="px-4 py-3">
                                    <div className="text-ink font-medium">{s.employee_name}</div>
                                    <div className="text-xs text-ink/50 truncate">
                                        {s.department || "—"}
                                    </div>
                                </td>
                                <td className="px-4 py-3 capitalize">{s.kind}</td>
                                <td className="px-4 py-3 hidden md:table-cell">{s.end_date}</td>
                                <td className="px-4 py-3 text-right tabular-nums font-display font-bold text-klein">
                                    {fmt(s.snapshot?.net_payable)}
                                </td>
                                <td className="px-4 py-3"><StatusPill status={s.status} /></td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center justify-end gap-1 flex-wrap">
                                        <button onClick={() => downloadPdf(s)} className="p-2 text-ink/60 hover:text-klein" title="Download PDF" data-testid={`settlement-pdf-${s.id}`}>
                                            <FileDown className="w-4 h-4" />
                                        </button>
                                        {canDisburse && (s.status === "pending_disbursement" || s.status === "disbursed") && (
                                            <button onClick={() => downloadAdvice(s)} className="p-2 text-ink/60 hover:text-klein" title="Bank advice CSV" data-testid={`settlement-advice-${s.id}`}>
                                                <FileSpreadsheet className="w-4 h-4" />
                                            </button>
                                        )}
                                        {s.status === "pending_dept_approval" && canDeptApprove && (
                                            <>
                                                <button onClick={() => action(s, "dept-approve")} className="p-2 text-green-700 hover:bg-green-50" title="Dept approve" data-testid={`settlement-dept-approve-${s.id}`}>
                                                    <Check className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => action(s, "reject", "Reason for rejection?")} className="p-2 text-red-700 hover:bg-red-50" title="Reject" data-testid={`settlement-dept-reject-${s.id}`}>
                                                    <Ban className="w-4 h-4" />
                                                </button>
                                            </>
                                        )}
                                        {s.status === "pending_finance_approval" && canFinanceApprove && (
                                            <>
                                                <button onClick={() => action(s, "finance-approve")} className="p-2 text-green-700 hover:bg-green-50" title="Finance approve" data-testid={`settlement-finance-approve-${s.id}`}>
                                                    <Check className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => action(s, "reject", "Reason for rejection?")} className="p-2 text-red-700 hover:bg-red-50" title="Reject" data-testid={`settlement-finance-reject-${s.id}`}>
                                                    <Ban className="w-4 h-4" />
                                                </button>
                                            </>
                                        )}
                                        {s.status === "pending_disbursement" && canDisburse && (
                                            <button
                                                onClick={() => action(s, "disburse", "Disbursement note (optional)")}
                                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs uppercase tracking-[0.12em] border border-klein text-klein hover:bg-klein hover:text-white"
                                                title="Mark as disbursed"
                                                data-testid={`settlement-disburse-${s.id}`}
                                            >
                                                <Send className="w-3.5 h-3.5" /> Disburse
                                            </button>
                                        )}
                                        {canDelete && (s.status === "pending_dept_approval" || s.status === "rejected") && (
                                            <button onClick={() => onDelete(s)} className="p-2 text-ink/60 hover:text-destructive hover:bg-destructive/5" title="Delete" data-testid={`settlement-delete-${s.id}`}>
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {open && (
                <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4" onClick={() => setOpen(false)} data-testid="settlement-create-dialog">
                    <form onSubmit={onCreate} onClick={(e) => e.stopPropagation()} className="bg-white border border-ink/10 w-full max-w-lg">
                        <div className="flex items-center justify-between px-8 py-6 border-b border-ink/10">
                            <div>
                                <div className="overline">New settlement</div>
                                <h3 className="mt-1 font-display font-bold text-xl">Initiate F&amp;F</h3>
                            </div>
                            <button type="button" onClick={() => setOpen(false)} className="p-2 text-ink/60 hover:bg-ink/5">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="px-8 py-6 space-y-5">
                            <div>
                                <label className="overline block mb-2">Employee *</label>
                                {selectedEmp ? (
                                    <div className="flex items-center justify-between border border-klein bg-klein/5 px-3 py-2.5">
                                        <div className="text-sm">
                                            <div className="font-medium text-ink">{selectedEmp.first_name} {selectedEmp.last_name}</div>
                                            <div className="text-xs text-ink/60">{selectedEmp.employee_no || "—"}{selectedEmp.department ? ` · ${selectedEmp.department}` : ""}</div>
                                        </div>
                                        <button type="button" onClick={() => { setForm({ ...form, employee_id: "" }); setEmpSearch(""); }} className="p-1 text-ink/50 hover:text-ink">
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <div className="relative">
                                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink/40 pointer-events-none" />
                                            <input
                                                type="search"
                                                placeholder="Search name, staff no, department, division, phone…"
                                                value={empSearch}
                                                onChange={(e) => setEmpSearch(e.target.value)}
                                                className="w-full border border-ink/15 py-2.5 pl-9 pr-3 text-sm focus:border-klein focus:outline-none"
                                                data-testid="settlement-emp-search"
                                                autoFocus
                                            />
                                        </div>
                                        <div className="mt-2 max-h-48 overflow-y-auto border border-ink/10">
                                            {filteredEmployees.length === 0 ? (
                                                <div className="px-3 py-4 text-xs text-ink/40">No matches.</div>
                                            ) : filteredEmployees.map((e) => (
                                                <button
                                                    key={e.id}
                                                    type="button"
                                                    onClick={() => { setForm({ ...form, employee_id: e.id }); setEmpSearch(""); }}
                                                    className="w-full text-left px-3 py-2 text-sm hover:bg-bone border-b border-ink/5 last:border-b-0"
                                                    data-testid={`settlement-emp-option-${e.id}`}
                                                >
                                                    <div className="font-medium text-ink">{e.first_name} {e.last_name}</div>
                                                    <div className="text-xs text-ink/50">{e.employee_no || "—"}{e.department ? ` · ${e.department}` : ""}</div>
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="overline block mb-2">Type *</label>
                                    <select
                                        value={form.kind}
                                        onChange={(e) => setForm({ ...form, kind: e.target.value })}
                                        className="w-full border border-ink/15 py-2.5 px-3 text-sm bg-transparent focus:border-klein focus:outline-none"
                                        data-testid="settlement-kind-input"
                                    >
                                        <option value="resignation">Resignation (1 month)</option>
                                        <option value="termination">Termination (3 months)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="overline block mb-2">Effective date *</label>
                                    <input
                                        type="date"
                                        value={form.end_date}
                                        onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                                        className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                        data-testid="settlement-end-date-input"
                                        required
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="overline block mb-2">Note for department lead</label>
                                <textarea
                                    rows="3"
                                    value={form.note}
                                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                                    placeholder="Context, reason, attachments etc."
                                    className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                />
                            </div>
                            <p className="text-xs text-ink/50">
                                The computation will be snapshotted on initiation. The dept lead will be emailed to review and approve.
                            </p>
                        </div>
                        <div className="px-8 py-5 border-t border-ink/10 flex items-center justify-end gap-3">
                            <button type="button" onClick={() => setOpen(false)} className="px-5 py-2.5 border border-ink/20 text-sm hover:bg-ink/5">Cancel</button>
                            <button type="submit" disabled={creating} className="inline-flex items-center gap-2 px-5 py-2.5 bg-klein text-white text-sm hover:bg-klein/90 disabled:opacity-60" data-testid="settlement-submit-button">
                                <Receipt className="w-4 h-4" /> {creating ? "Initiating…" : "Initiate"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
